import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router'

import { ExchangeShiftForm, ExchangeShiftOptions } from '@/containers'
import { getColleagues } from '@/helpers/api-ts/colleagues'
import { getShift, getShifts } from '@/helpers/api-ts/shiftsApi'
import { getEndOfWorkDay, getStartOfWorkDay } from '@/helpers/date'
import { ColleagueViewModel, DepartmentResource, ShiftViewModel } from '@/models'
import { submitShiftExchange } from '@/redux/exchanges/exchangesActions'
import { Reducers } from '@/redux/reducers'
import { offerShift } from '@/redux/shift/shiftActions'
import { useTypedDispatch as useLegacyTypedDispatch } from '@/redux/store'
import { filterShiftsByDepartments } from '@/services/ShiftService/filterShiftsByDepartments'

export const ConnectedExchangeShift: FC = () => {
    const dispatch = useLegacyTypedDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams<{ shiftUuid: string }>()
    const { currentUser, settings } = useSelector((state: Reducers) => state.userReducer)
    const { loading: storeLoading } = useSelector((state: Reducers) => state.exchangesReducer)

    const [showExchangeShiftForm, setShowExchangeShiftForm] = useState(false)
    const [loading, setLoading] = useState(false)
    const [employees, setEmployees] = useState<ColleagueViewModel[]>([])
    const [shifts, setShifts] = useState<ShiftViewModel[]>([])
    const [currentUserShiftPlanning, setCurrentUserShiftPlanning] = useState<string | null>(null)

    const getEmployees = async () => {
        if (! currentUser) {
            return
        }

        setLoading(true)
        const departments = currentUser.departments.map((department: DepartmentResource) => department.id)
        const data = await getColleagues(['employee'], departments, ['departments'])
        setEmployees(data.filter(colleague => colleague.id !== currentUser.id))
        setLoading(false)
    }

    const getShiftsByUserIdAndDate = async (userId: string, date: Date) => {
        if (! currentUser) {
            return
        }

        const userDepartments = currentUser.departments.map((department: DepartmentResource) => department.id)
        const startDate = getStartOfWorkDay(date)
        const endDate = getEndOfWorkDay(date)
        setLoading(true)
        const data = await getShifts(
            [userId],
            userDepartments,
            startDate,
            endDate,
            [
                'shiftPlannings',
                'shiftPlannings.user',
                'department',
                'department.group',
            ],
        )

        const filteredShifts = filterShiftsByDepartments(data, userDepartments)
        setShifts(filteredShifts)
        setLoading(false)
    }

    const getCurrentUserShift = async (shiftId: string, userId: string) => {
        const data = await getShift(
            shiftId,
            ['shiftPlannings', 'shiftPlannings.user'],
        )

        const userShiftPlanning = data?.shiftPlannings
            .find(shiftPlanning => shiftPlanning?.user?.id === userId)

        if (userShiftPlanning) {
            setCurrentUserShiftPlanning(userShiftPlanning.id)
        }
    }

    useEffect(() => {
        if (currentUser) {
            getEmployees()
        }
    }, [currentUser])

    useEffect(() => {
        if (params.shiftUuid && currentUser) {
            getCurrentUserShift(params.shiftUuid, currentUser.id)
        }
    }, [currentUser, params.shiftUuid])

    const onOfferShiftClick = () => {
        if (location.pathname) {
            const id = location.pathname.split('/')[2]

            dispatch(offerShift(id, navigate))
        }
    }

    const onChooseEmployeeClick = () => {
        setShowExchangeShiftForm(true)
    }

    const onSelectUserAndDate = (userId: string, date: Date) => {
        getShiftsByUserIdAndDate(userId, date)
    }

    const onSubmitShiftPlanning = (shiftPlanningId: string) => {
        if (currentUserShiftPlanning) {
            dispatch(submitShiftExchange(currentUserShiftPlanning, shiftPlanningId, navigate))
        }
    }

    if (showExchangeShiftForm) {
        return (
            <ExchangeShiftForm
                loading={loading || storeLoading}
                employees={employees}
                shifts={shifts}
                settings={settings}
                onSubmitShiftPlanning={onSubmitShiftPlanning}
                onSelectUserAndDate={onSelectUserAndDate}
            />
        )
    }

    return (
        <ExchangeShiftOptions
            onOfferShiftClick={onOfferShiftClick}
            onChooseEmployeeClick={onChooseEmployeeClick}
        />
    )
}
